/**
 * /v1/me/* — Self-service API wrapper.
 *
 * Used by Worker Portal (web) and future Mobile App. All endpoints
 * return data scoped to the authenticated worker — no admin permissions
 * required.
 */

import { ApiError, http } from './client';
import type { Schema } from './types';

interface ClockData {
  // Flat (web) — legacy
  lat?: number | null;
  lng?: number | null;
  address?: string | null;
  // Nested (mobile, ADR-018)
  geo?: { lat: number; lng: number; address?: string };
  photo_url?: string | null;
  face_template_match_score?: number | null;
  device_id?: string | null;
  is_mock_location?: boolean;
  /** ADR-030 Phase 2 — liveness session id when the client ran a risk-triggered
   *  Face Liveness scan for this punch (server verifies + uses its frame). */
  liveness_session_id?: string | null;
}

interface TaskCompleteData {
  outcome: string;
  comment?: string | null;
  form_data?: Record<string, unknown> | null;
}

interface CreateRequestData {
  process_definition_id?: string | null;
  title: string;
  priority?: string;
  /** Attachment IDs are keyed by field inside form_data; Backend has no top-level attachment map. */
  form_data?: Record<string, unknown> | null;
  cc_worker_ids?: string[] | null;
}

export type MeOrganizationLocation = Schema<'MeOrganizationLocationResponse'>;
export type MeDepartment = Schema<'MeDepartmentResponse'>;
export type MeShiftOption = Schema<'MeShiftOption'>;
export type MeShifts = Schema<'MeShiftsResponse'>;
/** One approval the current worker has already decided — the Reviewed half of the inbox. */
export type MeDecidedApproval = Schema<'MeDecidedApproval'>;
export type ProcessAttachment = Schema<'ProcessAttachmentResponse'>;
export type ProcessAttachmentUploadIntentRequest = Schema<'ProcessAttachmentUploadIntentRequest'>;
export type ProcessAttachmentUploadIntent = Schema<'ProcessAttachmentUploadIntentResponse'>;

interface TimeEntriesQuery {
  start_date?: string;
  end_date?: string;
}

interface ScheduleQuery {
  start_date?: string;
  end_date?: string;
}

interface ListQuery {
  status?: string;
}

// NEXT_SESSION P0.2 — Worker Dashboard aggregate. Type mirrors
// MeDashboardResponse on the platform side. Kept hand-typed (not
// pulled from schema.d.ts) so the worker portal's main view doesn't
// pay the openapi-typescript indirection on every render.
export interface DashboardResponse {
  today: {
    date: string;
    time_entry: { id: string; clock_in: string | null; clock_out: string | null; status?: string | null; total_hours?: number | null } | null;
    scheduled_in: string | null;        // 'HH:MM'
    scheduled_out: string | null;
    timetable_name: string | null;
    is_rest_day: boolean;
    is_holiday: boolean;
  };
  this_week: {
    week_start: string;
    week_end: string;
    scheduled_hours: number;
    actual_hours: number;
    by_day: Array<{ date: string; hours: number; status: string | null }>;
  };
  month_summary: {
    year_month: string;
    total_days: number;
    completed_days: number;
    late_days: number;
    absent_days: number;
    overtime_hours: number;
  };
  leave_balances: Array<{
    leave_type_id: string;
    leave_type_name: string;
    remaining: number;
    total: number;
    used: number;
  }>;
  /** Sums PLUS their components. The sums stay because svdy-mobile reads them as one total; the
   *  components exist so a count can sit next to the page that shows exactly it — the worker Overview
   *  card used to link the summed `requests` to the process list only (2026-09-05). */
  pending: {
    tasks: number; requests: number; approvals: number;
    attendance_requests: number; process_requests: number;
    attendance_approvals: number; process_approvals: number; face_approvals: number;
  };
  recent_announcements: Array<{ id: string; title: string; published_at: string | null }>;
  upcoming_events: Array<{ id: string; title: string; event_date: string | null }>;
  // P1.5: org's licensed_products surfaced here so the worker
  // sidebar can hide product sections (Attendance / Workflow)
  // for orgs that don't include them. Mirrors backend's mirror-
  // synced Organization.licensed_products. Default ['qutime'] for
  // new orgs that haven't been synced yet.
  licensed_products: string[];
}

// NEXT_SESSION P0.3 — month grid. Mirrors MeMonthScheduleResponse on
// the backend. Each day carries enough info to render a calendar cell
// with timetable colour-bar, scheduled in/out, rest-day flag, and
// holiday name.
export interface MonthDay {
  date: string;            // YYYY-MM-DD
  weekday: string;         // 'Mon'..'Sun'
  is_rest_day: boolean;
  is_cycle_off_day: boolean;
  is_holiday: boolean;
  holiday_name: string | null;
  schedule_type: 'fixed' | 'cycle' | 'temporary' | null;
  timetable_id: string | null;
  timetable_name: string | null;
  timetable_color: string | null;
  scheduled_in: string | null;       // 'HH:MM'
  scheduled_out: string | null;
}

export interface MonthScheduleResponse {
  year: number;
  month: number;            // 1..12
  first_date: string;
  last_date: string;
  days: MonthDay[];
}

// Worker-portal Holidays page (2026-06-26). Mirrors MeHolidaysResponse.
export interface MeHoliday {
  date: string;             // YYYY-MM-DD
  name: string;
  recurring: boolean;
}
export interface MeHolidaysResponse {
  year: number;
  holidays: MeHoliday[];
}

// Issue 3.3 (Path C 2026-05-22) — business days computation response.
// Mirrors MeBusinessDaysResponse in svdy-platform/app/me/schemas.py.
// Used by the New Request wizard to auto-populate `days_required`.
export interface MeBusinessDayItem {
  date: string;
  bucket: 'business_day' | 'weekend' | 'holiday';
  label?: string | null;
}

export interface MeBusinessDays {
  start_date: string;
  end_date: string;
  total_days: number;
  business_days: number;
  weekend_days: number;
  holiday_days: number;
  breakdown?: MeBusinessDayItem[] | null;
}

// NEXT_SESSION P0.6 — Worker organization context aggregate. Mirrors
// MeOrganizationResponse on the platform side. Hand-typed instead of
// pulled from schema.d.ts so the page rendering doesn't pay the
// openapi-typescript indirection on a hot path.
export interface OrgSelfBlock {
  worker_id: string;
  worker_code: string;
  name: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
  position: string | null;
  hire_date: string | null;
  department_id: string | null;
  manager_id: string | null;
}

export interface OrgPeer {
  worker_id: string;
  name: string;
  position: string | null;
  avatar_url: string | null;
  email: string | null;
  phone: string | null;
  is_self: boolean;
  is_manager: boolean;
}

export interface OrgManagerBlock {
  worker_id: string;
  name: string;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
  position: string | null;
  source: 'worker.manager_id' | 'department.manager_id' | string;
}

export interface OrgDepartmentBlock {
  id: string;
  name: string;
  code: string | null;
  description: string | null;
  parent_id: string | null;
  parent_name: string | null;
  manager_worker_id: string | null;
  member_count: number;
}

export interface OrgBranchBlock {
  id: string;
  name: string;
  code: string | null;
  address: string | null;
  is_primary: boolean;
}

export interface OrgLocationBlock {
  id: string;
  name: string;
  address: string | null;
  street_address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postal_code: string | null;
  timezone: string | null;
  phone: string | null;
  primary_contact_name: string | null;
  primary_contact_email: string | null;
}

export interface OrgOrgBlock {
  id: string;
  name: string;
  logo_url: string | null;
  industry: string | null;
  company_size: string | null;
  description: string | null;
  timezone: string | null;
  website: string | null;
}

export interface OrganizationResponse {
  self: OrgSelfBlock;
  department: OrgDepartmentBlock | null;
  manager: OrgManagerBlock | null;
  peers: OrgPeer[];
  branch: OrgBranchBlock | null;
  location: OrgLocationBlock | null;
  org: OrgOrgBlock;
}

// Worker self-centric organization chart. Keep the API wrapper on the
// generated OpenAPI contract so response defaults (optional arrays/counts) do
// not drift from FastAPI's serialized shape.
export type OrgChartPerson = Schema<'MeOrgChartPerson'>;
export type OrgChartTreeNode = Schema<'MeOrgChartTreeNode'>;
export type OrgChartResponse = Schema<'MeOrgChartResponse'>;

// Time Entries — daily diff (worker portal Time Entries rewrite).
// Mirrors MeTimeEntriesDailyResponse on the platform side.
export interface DayEntry {
  date: string;            // YYYY-MM-DD
  weekday: string;         // 'Mon'..'Sun'
  is_rest_day: boolean;
  is_cycle_off_day: boolean;
  is_holiday: boolean;
  holiday_name: string | null;
  timetable_id: string | null;
  timetable_name: string | null;
  scheduled_in: string | null;       // 'HH:MM'
  scheduled_out: string | null;
  scheduled_minutes: number;
  entry_id: string | null;
  clock_in: string | null;           // ISO datetime
  clock_out: string | null;
  total_hours: number | null;
  actual_minutes: number;
  entry_status: string | null;
  late_minutes: number;
  early_leave_minutes: number;
  missed_clock_in: boolean;
  missed_clock_out: boolean;
  status:
    | 'on_time' | 'late' | 'early_leave'
    | 'incomplete' | 'absent'
    // Approved full-day leave. Previously this day arrived as 'absent', because the
    // endpoint did not overlay approved leave at all.
    | 'leave'
    // Approved business trip with no punches. Same defect as 'leave' above, found and
    // closed 2026-08-25: the day arrived as 'absent' and was docked a full day, because
    // this endpoint derives status itself and only overlaid leave.
    | 'business_trip'
    | 'rest' | 'holiday' | 'unscheduled' | 'no_data'
    | 'scheduled';
  can_submit_missed_punch: boolean;
  notes: string | null;
  /** ADR-034 — payroll consequence (axis B), separate from status. 0/0.5/1.0. */
  deduction_days?: number;
  /** Stable reason code; the UI renders the localized explanation. */
  deduction_reason?:
    | 'absent_no_show' | 'missing_clock_out' | 'late_full_day' | 'early_full_day'
    | 'late_half_day' | 'early_half_day'
    | 'full_day_leave' | 'half_day_leave' | null;
  /** Privacy audit Wave 5 — presigned read URL for the worker's
   *  clock-in selfie on this day. Null when no photo was captured
   *  (web punches don't capture photos, or the org disabled photo
   *  capture in Attendance Rules). */
  clock_in_photo_url?: string | null;
}

export interface TimeEntriesDailyResponse {
  start_date: string;
  end_date: string;
  days: DayEntry[];
  total_scheduled_minutes: number;
  total_actual_minutes: number;
  total_late_minutes: number;
  total_early_leave_minutes: number;
  days_on_time: number;
  days_late: number;
  days_absent: number;
  days_incomplete: number;
}

// Profile expansion (Worker portal Profile rewrite).
export interface MeEmergencyContact {
  name: string | null;
  phone: string | null;
  relationship: string | null;
}

// MeHomeAddress removed — alembic 033 dropped the home_address_*
// columns (privacy audit 2026-05-22). See svdy/PRIVACY_AUDIT_2026-05-22.md.

export interface MeProfile {
  id: string;
  worker_code: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
  date_of_birth: string | null;
  position: string | null;
  department: string | null;
  employment_type: string | null;
  hire_date: string | null;
  status: string;
  emergency_contact: MeEmergencyContact;
  /** Privacy audit Wave 1 — biometric face template URL (presigned
   *  read URL on S3 backend, raw URL on local dev). Null when admin
   *  hasn't enrolled the worker. */
  face_template_url?: string | null;
  /** Privacy audit Wave 2 — GDPR Art. 9(2)(a) consent timestamp.
   *  Null = no consent → face matching disabled regardless of
   *  face_template_url presence. Worker portal Privacy tab renders
   *  the consent toggle state from this. */
  face_template_consented_at?: string | null;
  // ADR-027 — enrollment approval state (pending | approved | rejected | none).
  face_template_status?: string | null;
  face_template_rejection_reason?: string | null;
  /** ISO datetime when worker requested account deletion via
   *  /v1/me/account-deletion-request. Null when no request is in
   *  flight. Worker portal Privacy tab gates the deletion button on
   *  this. */
  deletion_requested_at?: string | null;
  /** Plan tier of the worker's organization. Worker portal uses this
   *  to gate Pro+ features (e.g. P0.3 email digest subscriptions
   *  require Professional tier). Source of truth: get_org_plan_tier
   *  helper. Workers don't have system.settings.read perm so the
   *  /v1/subscription/* surface isn't available — this field carries
   *  the value the worker needs to render tier-aware UX. */
  plan_tier?: string | null;
  /** Per-worker UI language (alembic 054). Null = inherit the org's
   *  language. The app PATCHes this whenever the worker switches their
   *  interface language so send-time channels (push / email) render in
   *  the worker's own language. */
  language?: string | null;
  /** alembic 055 — false for owners / salaried staff who don't clock in.
   *  Drives role derivation (useRoleProfile): an untracked user hides the
   *  personal punch / schedule surfaces. Defaults true (the core persona). */
  attendance_tracked?: boolean;
}

/** ADR-028 — one punch event (valid/invalid + reason) in the worker's day. */
export interface MePunch {
  id: string;
  punched_at: string;
  date: string;
  lat?: number | null;
  lng?: number | null;
  address?: string | null;
  within_geofence?: boolean | null;
  photo_url?: string | null;
  /** Horizontal GPS accuracy (m) at punch time — drawn as a faint ring. */
  accuracy?: number | null;
  face_status: string;
  face_match_score?: number | null;
  is_valid: boolean;
  invalid_reason?: string | null;
  source?: string | null;
  /** ADR-033 — clock_in | break_out | break_in | clock_out (server-classified). */
  punch_type?: string | null;
}

/** The attendance-approval row, taken from the EMITTED schema so the routed surface and the org-wide
 *  one cannot describe the same row two ways. */
export type AttendanceApprovalRow = Schema<'ApprovalResponse'>;

/** GET /v1/me/manager-overview — oversight summary for role derivation
 *  (mirrors MeManagerOverviewResponse). Reused by useRoleProfile to decide
 *  whether the worker sees Team / Approvals surfaces — the SAME signal the
 *  mobile app uses, so web and mobile gate identically (a role_code='worker'
 *  team lead with direct reports sees oversight surfaces on BOTH). */
export interface MeManagerOverview {
  is_manager: boolean;
  scope: string;            // 'organization' (admin-tier) | 'team'
  team_size: number;
  /** EMPLOYED direct reports, independent of attendance tracking.
   *
   *  The oversight ladder reads THIS, not `team_size` (2026-09-07). `team_size`
   *  is the attendance board size and is legitimately 0 on a tenant where
   *  nobody punches — which removed the Team button from a PureOA-only manager
   *  who has real reports, while /me/process-summary answered `scope='team'`
   *  for the same caller. Optional so an older backend degrades to `team_size`
   *  rather than to "no team". */
  reports_count?: number;
  clocked_in_today: number;
  pending_approvals: number;
  pending_attendance: number;
  pending_face_reviews: number;
  can_review: boolean;
  /** ANY attendance-approval authority: the org-wide permission, or being the resolved approver of at
   *  least one pending request (2026-09-07).
   *
   *  The To-do entry gates on THIS. `can_review` is "may open the ORG-WIDE queue", which the `worker`
   *  role must not hold — that endpoint returns every punch in the tenant with coordinates. But requests
   *  are routed by org chart to the requester's manager, so gating the entry on the permission hid the
   *  queue from the person the request was waiting on. Optional so an older backend degrades to
   *  `can_review` rather than hiding it from a permission holder. */
  attendance_reviewer?: boolean;
  /** The same, for the FACE queue. Counted separately on the server so a lead with a routed leave but
   *  no routed face check does not get an empty face surface (2026-09-08). */
  face_reviewer?: boolean;
}

/** GET /v1/me/team-exceptions — manager 'today's exceptions' triage board. */
export interface MeTeamExceptionWorker {
  id: string;
  name: string;
  department: string | null;
  scheduled_in?: string | null;
  clock_in?: string | null;
  late_minutes?: number | null;
  scheduled_out?: string | null;
}
export interface MeTeamPendingCorrection {
  request_id: string;
  worker_id: string;
  name: string;
  department: string | null;
  date: string | null;
  time: string | null;
  reason: string | null;
}
export interface MeTeamExceptions {
  scope: string;
  date: string;
  counts: { not_in: number; late: number; missing_out: number; pending_corrections: number };
  not_in: MeTeamExceptionWorker[];
  late: MeTeamExceptionWorker[];
  missing_out: MeTeamExceptionWorker[];
  pending_corrections: MeTeamPendingCorrection[];
}

/** ADR-027 — resolved clock-in policy from GET /v1/me/clock-in-config. */
export interface MeClockInConfig {
  face: {
    required: boolean;
    enrolled: boolean;
    status: string; // none | pending | approved | rejected
    threshold: number;
    max_retries: number;
    fail_policy: string; // block | pending_review | allow
  };
  photo: { required: boolean };
  liveness: { required: boolean };
  gps_required: boolean;
  geofence: {
    enforce: boolean;
    block_mock: boolean;
    fences: { name: string; lat: number; lng: number; radius_m: number }[];
  };
}

export interface MeProfileUpdate {
  phone?: string;
  avatar_url?: string;
  date_of_birth?: string;
  emergency_contact?: Partial<MeEmergencyContact>;
  language?: string;
}

export interface MeWorkerDocument {
  id: string;
  worker_id: string;
  document_type_id: string;
  file_name: string;
  file_url: string;
  file_size: number | null;
  uploaded_by: string | null;
  uploaded_at: string | null;
}

// Cross-org colleague search (P0.7 follow-up).
export interface MeColleague {
  worker_id: string;
  name: string;
  position: string | null;
  department: string | null;
  avatar_url: string | null;
  email: string | null;
  phone: string | null;
}

// Worker's own registered mobile devices (HANDOFF Addendum 18).
// Surfaced through /v1/me/devices for the Worker Portal Profile
// 'Devices' tab — lets the worker review every device and revoke
// any they no longer use. Distinct from the admin
// /v1/system/devices DELETE which has different permission gates.
export interface MeDeviceResponse {
  id: string;
  worker_id: string;
  platform: string;            // 'ios' | 'android' | 'web'
  device_id: string | null;
  app_version: string | null;
  registered_at: string;       // ISO datetime
  last_active_at: string;      // ISO datetime
  revoked_at: string | null;
}

function qs(params: object): string {
  const entries = Object.entries(params).filter(([, v]) => v != null) as [string, string][];
  return entries.length ? '?' + new URLSearchParams(entries).toString() : '';
}

export const meApi = {
  // Profile
  getProfile: () => http.get<MeProfile>('/v1/me/profile'),
  updateProfile: (data: MeProfileUpdate) =>
    http.patch<MeProfile>('/v1/me/profile', data),

  // Documents (worker portal Profile tab — own uploaded documents)
  documents: {
    list: () => http.get<MeWorkerDocument[]>('/v1/me/documents'),
  },

  // Cross-org colleague search (P0.7 follow-up — drives the
  // WorkerOrgChart search affordance: 'who is John Doe?').
  colleagues: {
    /**
     * `includeSelf` is off by default because CC and Forward must not offer the
     * requester. A `worker_reference` form field passes it: the seeded Offboarding
     * template makes `Departing Worker` required, so excluding self made a
     * self-filed resignation unfillable (2026-08-25).
     */
    search: (q: string, limit = 20, includeSelf = false) =>
      http.get<MeColleague[]>(
        `/v1/me/colleagues?q=${encodeURIComponent(q)}&limit=${limit}`
        + (includeSelf ? '&include_self=true' : ''),
      ),
  },

  // Today (legacy small aggregate — kept for back-compat)
  getToday: () => http.get('/v1/me/today'),

  // NEXT_SESSION P0.2 — full dashboard aggregate. One round-trip
  // returns today + this_week + month_summary + leave_balances +
  // pending counts + announcements + events. Mobile reuses verbatim.
  getDashboard: () => http.get<DashboardResponse>('/v1/me/dashboard'),

  // GET /v1/me/manager-overview — oversight summary (team_size / scope /
  // is_manager / can_review / pending_*). Drives useRoleProfile so web gates
  // Team/Approvals by ACTUAL oversight, not the coarse role_code. Mirrors
  // the mobile app's role derivation exactly.
  getManagerOverview: () => http.get<MeManagerOverview>('/v1/me/manager-overview'),

  /** THE SAME QUEUE, ASKED THE OTHER WAY (2026-09-07).
   *
   *  `/v1/attendance/approvals` is the ORG-WIDE queue, RBAC-gated on `attendance.approvals.read` — a
   *  permission the `worker` role must not hold, because that endpoint returns every punch in the tenant
   *  with coordinates. But an attendance request is ROUTED by org chart to the requester's manager, so a
   *  team lead on that role was the named approver of requests every reachable route refused.
   *
   *  These answer "waiting on ME", authorised by being the request's resolved approver — the same model
   *  PureOA's `/me/approvals/inbox` uses. A caller who is nobody's approver gets an empty list, which is
   *  why no permission is required and none is granted. Face rows are absent server-side (GDPR Art. 9). */
  attendanceApprovals: {
    list: (status: 'pending' | 'approved' | 'rejected' = 'pending') =>
      http.get<AttendanceApprovalRow[]>(`/v1/me/attendance-approvals?status=${status}`),
    approve: (id: string, note?: string) =>
      http.post<AttendanceApprovalRow>(`/v1/me/attendance-approvals/${id}/approve`, { note }),
    reject: (id: string, note?: string) =>
      http.post<AttendanceApprovalRow>(`/v1/me/attendance-approvals/${id}/reject`, { note }),
  },

  // GET /v1/me/team-exceptions — manager 'today's exceptions' triage board
  // (not-clocked-in / late / missing-clock-out + pending missed-punch
  // corrections). Role-scoped (admin → org; manager → direct reports).
  getTeamExceptions: () => http.get<MeTeamExceptions>('/v1/me/team-exceptions'),

  // Time entries
  timeEntries: {
    list: (q: TimeEntriesQuery = {}) =>
      http.get(`/v1/me/time-entries${qs(q)}`),
    /** Day-by-day expansion with scheduled-vs-actual diff and
     *  missing-punch flags. Drives the worker Time Entries page
     *  rewrite — gives visibility into days the worker didn't
     *  clock in (which the raw list silently dropped). */
    daily: (start_date: string, end_date: string) =>
      http.get<TimeEntriesDailyResponse>(
        `/v1/me/time-entries/daily?start_date=${start_date}&end_date=${end_date}`,
      ),
  },

  // Clock
  clockIn: (data: ClockData = {}) => http.post('/v1/me/clock-in', data),
  clockOut: (data: ClockData = {}) => http.post('/v1/me/clock-out', data),
  // ADR-028 — the single worker punch action. The worker never picks in vs
  // out; the calc derives clock-in (earliest valid punch) / clock-out (latest
  // valid punch) from the day's punch stream.
  punch: (data: ClockData = {}) => http.post('/v1/me/punch', data),
  /** ADR-030 + locked 2026-08-18 contract — liveness risk and explicit
   *  punch-device authorization are separate answers from the same preflight. */
  punchPolicy: (deviceId: string) =>
    http.get<Schema<'MePunchPolicyResponse'>>(
      `/v1/me/punch-policy?device_id=${encodeURIComponent(deviceId)}`,
    ),
  /** ADR-028 — the worker's own punches for a day (valid/invalid + reason). */
  punches: (date?: string) =>
    http.get<MePunch[]>(`/v1/me/punches${date ? `?date=${encodeURIComponent(date)}` : ''}`),
  /** ADR-028 — punches across an inclusive date range (Clock records history). */
  punchesRange: (start: string, end: string) =>
    http.get<MePunch[]>(`/v1/me/punches?start_date=${encodeURIComponent(start)}&end_date=${encodeURIComponent(end)}`),
  // ADR-027 — resolved clock-in policy (face/photo/gps/geofence) for the
  // calling worker; the UI branches + draws fences off this.
  getClockInConfig: () => http.get<MeClockInConfig>('/v1/me/clock-in-config'),

  // Clock-in photo (ADR-018)
  clockPhoto: {
    /** Get a one-shot upload URL. Client PUTs bytes to upload_url,
     *  then passes final_url back in clockIn({ photo_url: ... }). */
    presign: (contentType = 'image/jpeg') =>
      http.post<{
        key: string;
        upload_url: string;
        method: 'PUT';
        content_type: string;
        expires_in: number;
        final_url: string;
        requires_auth: boolean;
      }>(`/v1/me/clock-in-photo/presign?content_type=${encodeURIComponent(contentType)}`),
  },

  // Schedule
  schedule: {
    list: (q: ScheduleQuery = {}) =>
      http.get(`/v1/me/schedule${qs(q)}`),
    /** P0.3 — per-day expansion of the worker's month. Drives the
     *  Worker portal Schedule month grid + the future mobile app
     *  calendar. */
    month: (year: number, month: number) =>
      http.get<MonthScheduleResponse>(
        `/v1/me/schedule/month?year=${year}&month=${month}`,
      ),
  },

  // Leave balances
  leaveBalances: {
    list: () => http.get('/v1/me/leave-balances'),
  },

  // Business days computation (Issue 3.3 — Path C 2026-05-22). Used by
  // the New Request wizard to auto-populate `days_required` for leave-
  // style processes. Backend respects the org's AttendanceRule.rest_days
  // and PublicHoliday calendar; returns 400 on inverted ranges or > 366d.
  businessDays: {
    get: (start: string, end: string, detail = false) =>
      http.get<MeBusinessDays>(
        `/v1/me/business-days?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}${
          detail ? '&detail=true' : ''
        }`,
      ),
  },

  // Leave types + leave requests (P0.5) — first-class worker leave
  // submission, distinct from /me/requests because leave routes
  // through the attendance ApprovalRequest path and triggers the
  // P1.7 capacity preflight + LeaveBalance.used wiring.
  leaveTypes: {
    list: () => http.get('/v1/me/leave-types'),
  },
  // Worker-portal Holidays page — read-only org PublicHoliday calendar.
  holidays: {
    list: (year?: number) =>
      http.get<MeHolidaysResponse>(`/v1/me/holidays${year ? `?year=${year}` : ''}`),
  },
  leaveRequests: {
    create: (data: {
      leave_type: string;
      start_date: string;
      end_date: string;
      days?: number;
      duration?: 'full' | 'half_am' | 'half_pm';
      duration_days?: number;
      reason: string;
    }) => http.post('/v1/me/leave-requests', data),
  },

  // Organization (P0.6) — worker portal 'My Organization' aggregate
  organization: {
    get: () => http.get<OrganizationResponse>('/v1/me/organization'),
    /** P0.7 — self-centric org chart with ancestor chain + peers +
     *  descendant subtree. depth clamped to [0..5] server-side. */
    chart: (depth = 2) =>
      http.get<OrgChartResponse>(
        `/v1/me/organization/chart?depth=${depth}`,
      ),
  },

  // Tasks
  tasks: {
    list: (q: ListQuery = {}) =>
      http.get(`/v1/me/tasks${qs(q)}`),
    complete: (taskId: string, data: TaskCompleteData) =>
      http.post(`/v1/me/tasks/${taskId}/complete`, data),
  },

  // Requests
  requests: {
    list: (q: ListQuery & { q?: string } = {}) =>
      http.get(`/v1/me/requests${qs(q)}`),
    get: (id: string) =>
      http.get(`/v1/me/requests/${id}`),
    create: (data: CreateRequestData) =>
      http.post('/v1/me/requests', data),
    withdraw: (id: string) =>
      http.post(`/v1/me/requests/${id}/withdraw`, {}),
    cancel: (id: string) =>
      http.post(`/v1/me/requests/${id}/cancel`, {}),
    nudge: (id: string) =>
      http.post(`/v1/me/requests/${id}/nudge`, {}),
    resubmit: (id: string, data: { form_data?: Record<string, unknown> | null } = {}) =>
      http.post(`/v1/me/requests/${id}/resubmit`, data),
    comments: {
      list: (id: string) => http.get(`/v1/me/requests/${id}/comments`),
      create: (id: string, body: string) => http.post(`/v1/me/requests/${id}/comments`, { body }),
      delete: (id: string, commentId: string) => http.delete(`/v1/me/requests/${id}/comments/${commentId}`),
    },
  },

  // Available processes (for Submit Request form)
  availableProcesses: {
    list: () => http.get('/v1/me/available-processes'),
  },

  // Clock-in face reviews — the worker's OWN face enrollment + clock-in
  // face-review items + outcomes (read-only; adjudication is admin-side).
  clockInReviews: {
    list: () => http.get('/v1/me/clock-in-reviews'),
  },

  // Attendance requests — the worker's OWN submitted attendance/leave
  // requests (the 6 QuTime types) + their decision status. `create` uses the
  // same attendance ApprovalRequest path mobile uses: a missed_clock_in lands
  // in ATTENDANCE approvals and, once approved, actually corrects the record
  // (backend _apply_missed_punch_decision) — unlike the /me/requests process
  // path, which does neither.
  attendanceRequests: {
    list: (q: ListQuery = {}) => http.get(`/v1/me/attendance-requests${qs(q)}`),
    create: (data: { type: string; data: Record<string, unknown> }) =>
      http.post('/v1/me/attendance-requests', data),
    /** Withdraw the caller's own pending attendance request (parity w/ mobile). */
    withdraw: (id: string) =>
      http.post(`/v1/me/attendance-requests/${id}/withdraw`, {}),
  },

  // Team (M4.1 — manager view)
  team: {
    list: () => http.get('/v1/me/team'),
    /** Attendance aggregate for the manager's own scope, windowed.
     *
     * `today` counts PEOPLE (present incl. late, late, not_in); `week`/`month` count PERSON-DAYS from
     * the stored TimeEntry status projection, and `present_rate` is present_days / expected_days.
     * `top_late` / `top_absent` name the five most frequent, which is what answers "is this a
     * recurring problem with one person" — the phone has had this since team-report shipped and the
     * web worker portal had no month view at all.
     */
    summary: (period: 'today' | 'week' | 'month' = 'today') =>
      http.get(`/v1/me/team-summary?period=${period}`),
    /** PureOA process aggregate for the manager's own scope, windowed.
     *
     * Three scopes, and the response says which one it answered: `organization` for admin-tier, `team`
     * for a manager (their direct reports, excluding themselves), `self` for anyone with no reports.
     * Read `scope` rather than re-deriving the tier from the role — the server decides it, and
     * `useRoleProfile`'s `oversight` comes from a DIFFERENT endpoint, so the two can only agree by
     * coincidence.
     *
     * The `team` tier was added 2026-09-05, after a market audit found the personal ring this used to
     * hand a manager is a shape 0 of 10 comparable SMB products ship, while team-scoped approval
     * reporting for a line manager is normal (Zoho People's `Team Reports`, Factorial's "for people on
     * teams they lead").
     */
    processSummary: (period: 'today' | 'week' | 'month' = 'today') =>
      http.get(`/v1/me/process-summary?period=${period}`),
  },

  // Approvals inbox (M4.2)
  approvals: {
    inbox: (q: ListQuery = {}) =>
      http.get(`/v1/me/approvals/inbox${qs(q)}`),
    /** The "Reviewed" half: approvals I have already decided.
     *
     * NOT `inbox({ status: 'approved' })`, which the endpoint accepts and which would return
     * nothing useful: the inbox is scoped to the CURRENT approver, and a decided request has
     * advanced past me. The decision lives on the task I actioned. */
    decided: (limit = 100) =>
      http.get(`/v1/me/approvals/decided?limit=${limit}`) as Promise<MeDecidedApproval[]>,
    approve: (requestId: string, data: { notes?: string } = {}) =>
      http.post(`/v1/me/approvals/${requestId}/approve`, data),
    reject: (requestId: string, data: { reason: string }) =>
      http.post(`/v1/me/approvals/${requestId}/reject`, data),
    delegate: (requestId: string, data: { delegated_to: string; justification?: string; expires_at?: string }) =>
      http.post(`/v1/me/approvals/${requestId}/delegate`, data),
  },


  // Request-form tenant references and private attachments.
  organizationLocations: {
    listActive: () => http.get<MeOrganizationLocation[]>('/v1/me/organization-locations'),
  },
  /** Active departments a `department_reference` field can offer. */
  departments: {
    listActive: () => http.get<MeDepartment[]>('/v1/me/departments'),
  },
  /**
   * The worker's resolved shift for a date plus every timetable they could switch to.
   * Mobile twin: `getMyShifts` — the `schedule_change` request needs both halves, and
   * this client typed them by hand until 2026-08-24.
   */
  shifts: {
    get: (isoDate?: string) => http.get<MeShifts>(
      `/v1/me/shifts${isoDate ? `?date=${encodeURIComponent(isoDate)}` : ''}`,
    ),
  },
  processAttachments: {
    createIntent: (data: ProcessAttachmentUploadIntentRequest) =>
      http.post<ProcessAttachmentUploadIntent>('/v1/me/process-attachments/upload-intents', data),
    upload: async (intent: ProcessAttachmentUploadIntent, file: File) => {
      const headers = {
        'Content-Type': intent.content_type,
        ...(intent.required_headers ?? {}),
      };
      if (intent.requires_auth) {
        await http.put(intent.upload_url, file, { headers });
        return;
      }
      const response = await fetch(intent.upload_url, { method: intent.method ?? 'PUT', headers, body: file });
      if (!response.ok) throw new ApiError(response.status, { detail: response.statusText }, intent.upload_url);
    },
    download: (attachmentId: string) =>
      http.get<Blob>(`/v1/me/process-attachments/${attachmentId}/download`, { responseType: 'blob' }),
    removeStaged: (attachmentId: string) =>
      http.delete<void>(`/v1/me/process-attachments/${attachmentId}`),
  },
  // Notification channel preferences (NotificationPreference.channels
  // JSONB). Backend ships defaults `{in_app, email, push, whatsapp}`
  // with the first three True (whatsapp False) — surfaced through
  // /v1/notifications/preferences. The fanout dispatch layer
  // (ADR-021 D5) honours channels.push for push leg suppression.
  // Worker portal Profile screen consumes these for self-service.
  notificationPreferences: {
    get: () =>
      http.get<{ channels: Record<string, boolean> } | null>(
        '/v1/notifications/preferences',
      ),
    save: (channels: Record<string, boolean>) =>
      http.put<{ channels: Record<string, boolean> }>(
        '/v1/notifications/preferences',
        { channels },
      ),
  },

  // Worker's own registered mobile devices (HANDOFF Addendum 18).
  // Worker Portal Profile 'Devices' tab lets the worker review
  // every device tied to their account + revoke any that they
  // no longer use (different from the admin force-revoke at
  // /v1/system/devices/{id}, which is for lost / stolen). The
  // revoked rows stay for audit; re-registering on the next
  // mobile sign-in flips revoked_at back to null.
  devices: {
    list: (includeRevoked = false) =>
      http.get<MeDeviceResponse[]>(
        `/v1/me/devices?include_revoked=${includeRevoked ? 'true' : 'false'}`,
      ),
    revoke: (deviceId: string) =>
      http.delete<MeDeviceResponse>(`/v1/me/devices/${deviceId}`),
  },

  // GDPR — self-service data export + account deletion (S7 §2.2).
  // Exposed on the worker portal Profile "Privacy" tab. Backend
  // returns inline JSON for small orgs (Phase 1); a future phase
  // routes >5 MB payloads through arq → S3 → SES email link.
  // Account deletion sets deletion_requested_at = now() and the
  // retention purge cron hard-anonymizes after the 30-day grace.
  gdpr: {
    exportData: () =>
      http.get<{
        exported_at: string;
        worker_profile: Record<string, unknown>;
        time_entries: Array<Record<string, unknown>>;
        attendance_summaries: Array<Record<string, unknown>>;
        process_requests: Array<Record<string, unknown>>;
      }>('/v1/me/data-export'),
    requestAccountDeletion: () =>
      http.post<{
        status: string;
        deletion_requested_at: string;
        message: string;
      }>('/v1/me/account-deletion-request', {}),
  },

  // Privacy audit Wave 2 — biometric consent (GDPR Art. 9(2)(a)).
  // Worker self-grants and self-revokes; no admin-on-behalf path.
  // Issue 4 (Path C 2026-05-22) — added self-upload via the new
  // /me/face-template page; auto-grants consent server-side.
  faceTemplateConsent: {
    grant: () => http.post<MeProfile>('/v1/me/face-template/consent', {}),
    revoke: () => http.delete<MeProfile>('/v1/me/face-template/consent'),
    upload: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return http.post<MeProfile>('/v1/me/face-template/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    // ADR-030 — in-browser liveness enrollment. Mint a session the web
    // FaceLivenessDetector runs, then enroll straight from the passed session
    // (its live reference frame becomes the template — no photo file picked,
    // so web enrollment is gated by the same liveness as mobile).
    createLivenessSession: () =>
      http.post<{ session_id: string }>('/v1/me/face-liveness/session', {}),
    enrollFromLiveness: (sessionId: string) =>
      http.post<MeProfile>('/v1/me/face-template/from-liveness', {
        liveness_session_id: sessionId,
      }),
  },

};
